import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import {
  analytics,
  getSearchOpenDetail,
  getNavigationOpenDetail,
} from '@/utils/analytics';

import { getContent } from '@/utils/helper';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';
import Button from '@/atoms/Button/Button';
import WestholmeLogo from '@/atoms/Logo/WestholmeLogo';
import DDLogo from '@/atoms/Logo/DDLogo';
import { CtaWrapper } from '@/molecules/CTA/index';
import Search from '@/molecules/Search/Search';
import LangSwitcher from '@/molecules/LangSwitcher/LangSwitcher';
import NavigationWithSubMenu from '@/molecules/NavigationWithSubMenu/NavigationWithSubMenu';

function NavigationSticky({ fields = [], entries = [], hasBackground, slug }) {
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [navigationIsOpen, setNavigationIsOpen] = useState(false);
  const { domainLocales, asPath, locales, locale } = useRouter();

  const toggleNavigation = (e) => {
    if (!navigationIsOpen) {
      analytics({
        window,
        document,
        detail: getNavigationOpenDetail(e),
      });
      setNavigationIsOpen(true);
    } else {
      setNavigationIsOpen(false);
    }
  };

  const closeNavigation = () => setNavigationIsOpen(false);

  const openSearch = (e) => {
    analytics({
      window,
      document,
      detail: getSearchOpenDetail(e),
    });
    setSearchIsOpen(true);
  };

  useEffect(() => {
    if (searchIsOpen || navigationIsOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => (document.body.style.overflow = 'unset');
  }, [searchIsOpen, navigationIsOpen]);

  const navigationMenu =
    fields?.links?.length > 0
      ? fields.links.map((link) => {
          const menuContent = getContent(link.sys.id, entries)?.fields;

          // get page preview & url per link
          const page =
            menuContent?.pages && menuContent?.pages.length
              ? getContent(menuContent?.pages[0].sys.id, entries)
              : menuContent?.page
              ? getContent(menuContent?.page?.sys?.id, entries)
              : null;
          return {
            ...menuContent,
            pageUrl: page?.fields?.slug,
            pageContent: {
              cloudinaryPreviewAssets: page?.fields?.cloudinaryPreviewAssets,
              previewAsset: page?.fields?.previewAsset,
              previewText: getContent(
                page?.fields?.previewText?.sys?.id,
                entries
              ),
            },
          };
        })
      : [];

  return (
    <div
      className={`sticky z-50 top-0 w-full h-20 bg-white dd-kr:bg-progressive-blue dd-en:bg-progressive-blue`}
      key={slug}
    >
      <div className="relative z-40">
        <nav id="navigation" className="nav-menu">
          <div
            className={`w-full px-[30px] py-[10px] dd-kr:bg-progressive-blue dd-en:bg-progressive-blue`}
          >
            <div className="flex items-center justify-center dd-kr:px-0 dd-en:px-0">
              <div className="nav-menu__logo cursor-pointer w-fit order-2 lg:absolute lg:left-[30px] lg:top-[10px]">
                <CtaWrapper
                  page="/"
                  classNames="block dd-kr:hidden dd-en:hidden"
                >
                  <WestholmeLogo
                    color={hasBackground ? '#fff' : '#000'}
                    width={125}
                    height={78}
                  />
                </CtaWrapper>
                <CtaWrapper
                  page="/"
                  classNames="hidden dd-kr:block dd-en:block"
                  reloadPage
                >
                  <DDLogo width={51.05} height={60} />
                </CtaWrapper>
              </div>
              <Button
                invert={hasBackground}
                style="lg:hidden nav-menu__btn cursor-pointer dd-kr:text-white dd-en:text-white w-fit absolute left-[30px] top-[30px]"
                onClick={toggleNavigation}
              >
                {navigationIsOpen ? (
                  <Icon name="close" height={24} width={24} />
                ) : (
                  <Icon name="hamburger" height={24} width={24} />
                )}
              </Button>
              <NavigationWithSubMenu
                entries={entries}
                navigationMenu={navigationMenu}
                closeNavigation={closeNavigation}
                style={`hidden lg:flex h-[60px]`}
                reloadPage
              />
              <div className="nav-menu__actions flex justify-end items-center gap-x-[25px] absolute right-[30px] top-5">
                <div
                  role={'button'}
                  onClick={openSearch}
                  className="hidden lg:block"
                >
                  <Icon
                    name="search"
                    height={18}
                    width={18}
                    className={`w-6 h-6 md:w-4 h:4 text-white translate-y-[1.5px]`}
                  />
                </div>
                <LangSwitcher
                  domains={domainLocales}
                  locales={locales}
                  locale={locale}
                  path={asPath}
                  style={'hidden lg:flex'}
                />
                {navigationMenu.length > 1 && (
                  <CtaWrapper
                    page={ navigationMenu[navigationMenu.length - 1]?.pageUrl && `/${
                      navigationMenu[navigationMenu.length - 1]?.pageUrl
                    }`}
                    url={navigationMenu[navigationMenu.length - 1]?.url}
                    classNames="nav-menu aaco-cta link-invert flex items-center md:bg-majesty-5 dd-en:bg-actual-dd-gold dd-kr:bg-actual-dd-gold md:h-10 md:px-5 dd-en:rounded-[100px] dd-kr:rounded-[100px] cursor-pointer md:w-15 justify-self-end dd-en:py-[9px] dd-kr:py-[9px] dd-en:px-4 dd-kr:px-4 font-text font-button text-button text-white w-max"
                    reloadPage
                  >
                    {navigationMenu[navigationMenu.length - 1].name}
                  </CtaWrapper>
                )}
              </div>
            </div>
          </div>
        </nav>
        <div
          className={`${
            navigationIsOpen ? 'block' : 'hidden'
          } lg:hidden aaco-mobile-nav absolute origin-top w-full bg-progressive-blue-40 p-[30px] flex flex-col justify-center`}
        >
          <div className="flex flex-col w-full">
            <div
              role={'button'}
              onClick={openSearch}
              className="flex gap-x-[15px] pb-[18px] mb-[15px] items-center border-b-1 border-b-40-white-on-dark-blue"
            >
              <Icon
                name="search"
                height={18}
                width={18}
                className={`w-[18px] h-[18px] text-white translate-y-[1px]`}
              />
              <Text tag={'body'} style="text-white">
                {locale === 'ko-KR' ? '검색하기' : 'Search'}
              </Text>
            </div>
            <NavigationWithSubMenu
              entries={entries}
              navigationMenu={navigationMenu}
              closeNavigation={closeNavigation}
              style={`flex flex-col`}
              reloadPage
            />
          </div>
          <LangSwitcher
            domains={domainLocales}
            locales={locales}
            locale={locale}
            path={asPath}
            style={'flex lg:hidden self-start'}
          />
        </div>
      </div>
      <Search
        open={searchIsOpen}
        closeSearch={() => {
          setSearchIsOpen(false);
        }}
        closeNavigation={() => {
          setSearchIsOpen(false);
          setNavigationIsOpen(false);
        }}
        tagGroupsWithTags={fields?.tagGroupsWithTags}
      />
    </div>
  );
}

// @TODO: define real props for the Navigation based on it requirements
NavigationSticky.propTypes = {
  entries: PropTypes.array,
  fields: PropTypes.object,
  hasBackground: PropTypes.bool,
  tagGroupsWithTags: PropTypes.array,
  slug: PropTypes.string,
};

NavigationSticky.defaultProps = {
  hasBackground: false,
};

export default NavigationSticky;
